/**
 * CityAutocomplete
 *
 * Typeahead over `/api/cities/suggest` — used in two places:
 *   • Firm-side deadline `city` input       → `multi={false}` (single string)
 *   • Student Talent Profile target_cities  → `multi={true}`  (chip array)
 *
 * Free-text fallback: users can Enter/Tab/comma-commit an unlisted city
 * (the backend accepts any string; the endpoint is purely a hint).
 *
 * Props:
 *   multi?: boolean                (default false)
 *   value?: string                 (single mode — required when multi=false)
 *   values?: string[]              (multi mode  — required when multi=true)
 *   onChange: (string | string[]) => void
 *   placeholder?: string
 *   maxChips?: number              (multi only — default 3)
 *   maxLength?: number             (single mode — default 80)
 *   testIdPrefix?: string
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { X } from "lucide-react";


export default function CityAutocomplete({
  multi = false,
  value = "",
  values = [],
  onChange,
  placeholder = "Search cities…",
  maxChips = 3,
  maxLength = 80,
  testIdPrefix = "city-picker",
}) {
  const [query, setQuery] = useState(multi ? "" : value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const canAddMore = multi ? values.length < maxChips : true;

  // Keep single-mode input in sync when parent resets value externally.
  useEffect(() => {
    if (!multi) setQuery(value || "");
  }, [multi, value]);

  // Debounced fetch.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/cities/suggest", {
          params: { q: (query || "").trim(), limit: 10 },
        });
        const picked = multi
          ? new Set((values || []).map((v) => (v || "").toLowerCase()))
          : new Set();
        setSuggestions(
          (data || []).filter((s) => !picked.has((s.name || "").toLowerCase())),
        );
      } catch {
        setSuggestions([]);
      }
    }, 150);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, open, multi ? values.join("|") : ""]);

  // Outside click closes dropdown.
  useEffect(() => {
    const onDocClick = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const commit = (name) => {
    const nm = (name || "").trim().slice(0, maxLength);
    if (!nm) return;
    if (multi) {
      if (!canAddMore) return;
      if ((values || []).some((v) => v.toLowerCase() === nm.toLowerCase())) return;
      onChange([...(values || []), nm]);
      setQuery("");
      setActiveIdx(0);
      inputRef.current?.focus();
    } else {
      onChange(nm);
      setQuery(nm);
      setOpen(false);
    }
  };

  const removeChip = (nm) => {
    if (!multi) return;
    onChange((values || []).filter((v) => v !== nm));
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (suggestions[activeIdx]) commit(suggestions[activeIdx].name);
      else if (query.trim()) commit(query.trim());
    } else if (e.key === "Backspace" && multi && !query && values.length) {
      removeChip(values[values.length - 1]);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Escape") {
      setOpen(false);
    } else if (multi && (e.key === "," || e.key === "Tab")) {
      if (query.trim()) { e.preventDefault(); commit(query.trim()); }
    }
  };

  // ── Render ─────────────────────────────────────────────────────
  return (
    <div ref={containerRef} className="relative" data-testid={testIdPrefix}>
      <div
        className={
          "flex flex-wrap gap-1.5 border border-stone-300 rounded-md bg-white " +
          "px-2 py-1.5 min-h-[40px] focus-within:border-[#4C1D95] " +
          "focus-within:ring-2 focus-within:ring-[#4C1D95]/20 transition-colors"
        }
        onClick={() => inputRef.current?.focus()}
      >
        {multi && (values || []).map((v) => (
          <span
            key={v}
            data-testid={`${testIdPrefix}-chip-${v.toLowerCase().replace(/\s+/g, "-")}`}
            className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-900 px-2.5 py-1 text-[12px]"
          >
            <span>{v}</span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeChip(v); }}
              aria-label={`Remove ${v}`}
              data-testid={`${testIdPrefix}-remove-${v.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-emerald-700 hover:text-red-600"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          data-testid={`${testIdPrefix}-input`}
          className="flex-1 min-w-[120px] bg-transparent outline-none text-sm px-1 py-0.5"
          value={query}
          maxLength={maxLength}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            if (!multi) onChange(e.target.value);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          onBlur={() => {
            // Multi mode: commit the pending free-text as a chip so users
            // don't lose what they typed when they click away.
            if (multi) {
              const q = query.trim();
              if (q) commit(q);
            }
          }}
          placeholder={multi && values.length ? "" : placeholder}
          disabled={!canAddMore}
        />
      </div>

      {multi && (
        <div className="text-xs text-stone-500 mt-1 font-mono">
          {values.length}/{maxChips} · press Enter or comma to add
        </div>
      )}

      {open && suggestions.length > 0 && canAddMore && (
        <div
          className="absolute left-0 right-0 mt-1 bg-white border border-stone-200 rounded-md shadow-lg z-50 max-h-72 overflow-y-auto"
          data-testid={`${testIdPrefix}-dropdown`}
        >
          {suggestions.map((s, i) => (
            <button
              type="button"
              key={`${s.name}-${s.slug || i}`}
              data-testid={`${testIdPrefix}-option-${(s.name || "").toLowerCase().replace(/\s+/g, "-")}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => commit(s.name)}
              onMouseEnter={() => setActiveIdx(i)}
              className={
                "w-full text-left px-3 py-2 text-sm border-b border-stone-100 last:border-b-0 transition-colors " +
                (i === activeIdx ? "bg-[#F3F1EC]" : "bg-white hover:bg-stone-50")
              }
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium truncate">{s.name}</div>
                  {s.country && (
                    <div className="text-[11px] text-stone-500 font-mono truncate">
                      {s.country}
                    </div>
                  )}
                </div>
                {s.is_guide && (
                  <span className="text-[10px] font-mono text-emerald-700 shrink-0">
                    guide
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export { CityAutocomplete };
